import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Avatar } from '../components/Avatar';
import { ArrowLeft, Lock, Play, Search, Star, Timer } from 'lucide-react';
import { expiryLabel } from '../lib/disappearing';
import { isExpired } from '@twinden/shared';
import { useNavigate } from 'react-router-dom';
import { useAtom, useSetAtom } from 'jotai';
import { fullScreenPageAtom, starredByFamilyAtom } from '../store';
import { useAuth, useFamily, useLoadingState } from '../hooks';
import { LoadingPanel } from '../components/brand/TwindenLoader';
import { api, ApiError, type StarredMessage } from '../lib/api';
import { mediaSecrets, useMediaUrl } from '../lib/e2ee/media';
import { NoFamilySelected } from '../components/family/NoFamilySelected';
import { EmptyState } from '../components/EmptyState';
import { appLocale } from '../lib/format';
import { t as translateNow, useT } from '../i18n';
import { isKnownType, replyLabel } from '../components/chat/messageUtils';
import { isUnbound, UnboundMark } from '../components/UnboundMark';

type Translate = ReturnType<typeof useT>;

function messagePreview(starred: StarredMessage, t: Translate): string {
  const { message } = starred;
  const metadata = (message.metadata ?? {}) as { fileName?: string };

  switch (message.type) {
    case 'image':
    case 'gif':
      return t('apps.starred.photo');
    case 'video':
      return t('apps.starred.video');
    case 'voice':
      return t('apps.starred.voice');
    case 'file':
      return metadata.fileName ?? t('apps.starred.file');
    // M60: "Photos" or the caption, "Poll: …", and nothing raw from a type
    // this app doesn't know (D222).
    case 'album':
    case 'poll':
    case 'gathering':
      return replyLabel(message);
    default:
      return isKnownType(message.type)
        ? message.content
        : t('chat.type.unknown');
  }
}

// "Today", "Mon", "9 Oct".
function shortDate(iso: string, t: Translate, now = new Date()): string {
  const date = new Date(iso);
  if (date.toDateString() === now.toDateString()) return t('apps.shared.today');
  if ((now.getTime() - date.getTime()) / 86_400_000 < 7) {
    return date.toLocaleDateString(appLocale(), { weekday: 'short' });
  }
  return date.toLocaleDateString(appLocale(), {
    day: 'numeric',
    month: 'short',
  });
}

function formatDuration(seconds?: number): string {
  const total = Math.round(seconds ?? 0);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

// A starred photo's preview, decrypted on the device when needed (M28).
function StarredPhoto({ message }: { message: StarredMessage['message'] }) {
  const meta = (message.metadata ?? {}) as { thumbnailUrl?: string };
  const secrets = mediaSecrets(message);
  const preview = useMediaUrl(
    meta.thumbnailUrl ?? message.content,
    meta.thumbnailUrl ? secrets.thumb : secrets.file,
    meta.thumbnailUrl ? 'image/jpeg' : secrets.mimeType
  );
  return (
    <Image
      src={preview.src}
      alt=""
      w="72px"
      h="56px"
      objectFit="cover"
      borderRadius="12px"
      flexShrink={0}
      bg="gray.200"
    />
  );
}

// Starred (Living Room): your personal keepsakes from every chat, each with
// the chat it came from. Full screen, with its own back button.
export function StarredPage() {
  const { currentFamilyId } = useFamily();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const [starredMap, setStarredMap] = useAtom(starredByFamilyAtom);
  // True from the start when there is a family to ask: the empty state
  // must never flash before the loader's empty area (M66, D248).
  const [loading, setLoading] = useState(Boolean(currentFamilyId));
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);

  const items = currentFamilyId ? (starredMap[currentFamilyId] ?? []) : [];

  const setFullScreen = useSetAtom(fullScreenPageAtom);
  useEffect(() => {
    setFullScreen(true);
    return () => setFullScreen(false);
  }, [setFullScreen]);

  const load = useCallback(
    async (familyId: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.getStarred(familyId);
        setStarredMap((current) => ({
          ...current,
          [familyId]: response.data,
        }));
        setCursor(response.pagination.cursor);
        setHasMore(response.pagination.hasMore);
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : translateNow('apps.starred.loadFailed')
        );
      } finally {
        setLoading(false);
      }
    },
    [setStarredMap]
  );

  // The mark while the first page loads; what's cached for the family shows at once (M66, D248).
  const starredState = useLoadingState({
    loading,
    hasData: items.length > 0,
    error,
    retry: () => {
      if (currentFamilyId) void load(currentFamilyId);
    },
  });
  useEffect(() => {
    setCursor(undefined);
    setHasMore(false);
    if (currentFamilyId) void load(currentFamilyId);
  }, [currentFamilyId, load]);

  const loadMore = async () => {
    if (!currentFamilyId || !cursor) return;
    setLoadingMore(true);
    try {
      const response = await api.getStarred(currentFamilyId, cursor);
      setStarredMap((current) => ({
        ...current,
        [currentFamilyId]: [
          ...(current[currentFamilyId] ?? []),
          ...response.data,
        ],
      }));
      setCursor(response.pagination.cursor);
      setHasMore(response.pagination.hasMore);
    } catch (err) {
      toast({
        title: t('apps.starred.loadMoreFailed'),
        description:
          err instanceof ApiError ? err.message : t('apps.shared.unknownError'),
        status: 'error',
      });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleUnstar = async (starred: StarredMessage) => {
    if (!currentFamilyId) return;
    try {
      await api.unstarMessage(starred.message.id);
      setStarredMap((current) => ({
        ...current,
        [currentFamilyId]: (current[currentFamilyId] ?? []).filter(
          (entry) => entry.message.id !== starred.message.id
        ),
      }));
    } catch (err) {
      toast({
        title: t('apps.starred.unstarFailed'),
        description:
          err instanceof ApiError ? err.message : t('apps.shared.unknownError'),
        status: 'error',
      });
    }
  };

  const openConversation = (starred: StarredMessage) => {
    navigate(`/?conversation=${starred.conversation.id}`);
  };

  // Search within the starred view (client-side: it's a short, personal list).
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const shown = items.filter(
    (starred) =>
      // A star goes with its disappearing message (D238).
      !isExpired(starred.message.expiresAt) &&
      (!query.trim() ||
        messagePreview(starred, t)
          .toLowerCase()
          .includes(query.trim().toLowerCase()) ||
        (starred.message.sender?.displayName ?? '')
          .toLowerCase()
          .includes(query.trim().toLowerCase()))
  );

  if (!currentFamilyId) return <NoFamilySelected />;

  const chatName = (starred: StarredMessage) =>
    starred.conversation.isDefault
      ? t('apps.starred.everyone')
      : (starred.conversation.name ??
        // A DM has no name; the other person's is only known when they sent it.
        (starred.message.sender && starred.message.sender.id !== currentUser?.id
          ? starred.message.sender.displayName
          : t('apps.starred.directMessage')));

  return (
    <Flex direction="column" h="full" maxW="720px" mx="auto" w="full">
      <HStack
        spacing={1}
        px={2}
        pt={{ base: 'max(env(safe-area-inset-top), 0.5rem)', md: 2 }}
        minH="56px"
        flexShrink={0}
      >
        <IconButton
          aria-label={t('common.back')}
          icon={<ArrowLeft size={24} />}
          variant="ghost"
          colorScheme="gray"
          onClick={() => navigate(-1)}
        />
        <Box flex="1" />
        <IconButton
          aria-label={
            searchOpen ? t('apps.shared.closeSearch') : t('apps.starred.search')
          }
          aria-pressed={searchOpen}
          icon={<Search size={22} />}
          variant="ghost"
          colorScheme="gray"
          onClick={() => {
            setSearchOpen((open) => !open);
            if (searchOpen) setQuery('');
          }}
        />
      </HStack>

      <VStack align="stretch" spacing={1} px={4} pb={4} flexShrink={0}>
        <Heading as="h1" fontSize="32px" lineHeight={1}>
          {t('apps.starred.title')}
        </Heading>
        <HStack spacing="6px" fontSize="14px" color="muted">
          <Lock size={14} aria-hidden="true" />
          <Text>{t('apps.starred.onlyYou')}</Text>
        </HStack>
        {searchOpen && (
          <InputGroup pt={2}>
            <InputLeftElement pointerEvents="none" color="muted" top={2}>
              <Search size={18} />
            </InputLeftElement>
            <Input
              autoFocus
              placeholder={t('apps.starred.search')}
              aria-label={t('apps.starred.search')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              borderRadius="full"
            />
          </InputGroup>
        )}
      </VStack>

      <Box
        flex="1"
        minH={0}
        overflowY="auto"
        px={4}
        pb={6}
        sx={{ overscrollBehaviorY: 'contain' }}
      >
        {error && !starredState.failed && (
          <Alert status="error" borderRadius="xl" mb={3}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        {starredState.show ? (
          <LoadingPanel
            state={starredState}
            label={t('apps.starred.loading')}
          />
        ) : items.length === 0 ? (
          <EmptyState
            kind="starred"
            title={t('apps.starred.emptyTitle')}
            body={t('apps.starred.emptyBody')}
          />
        ) : (
          <VStack align="stretch" spacing="10px">
            {shown.map((starred) => {
              const { message } = starred;
              const sender =
                message.sender?.displayName ?? t('apps.starred.unknown');
              const isPhoto =
                message.type === 'image' || message.type === 'gif';
              const meta = (message.metadata ?? {}) as {
                thumbnailUrl?: string;
                duration?: number;
              };
              return (
                <Box
                  key={message.id}
                  bg="surface"
                  borderWidth="1px"
                  borderColor="line"
                  borderRadius="card"
                  p="14px"
                >
                  <HStack spacing={2} fontSize="13px" color="muted" mb={2}>
                    <Text
                      as="span"
                      h="24px"
                      px="10px"
                      borderRadius="full"
                      bg={
                        starred.conversation.isDefault
                          ? 'accent.tint'
                          : 'surface.subtle'
                      }
                      color={
                        starred.conversation.isDefault ? 'accent' : 'ink.body'
                      }
                      fontWeight={700}
                      display="inline-flex"
                      alignItems="center"
                      noOfLines={1}
                      maxW="60%"
                    >
                      {chatName(starred)}
                    </Text>
                    <Text as="span" flex="1">
                      {shortDate(message.createdAt, t)}
                      {message.expiresAt && (
                        // It disappears, star and all (D238).
                        <Box
                          as="span"
                          display="inline-flex"
                          verticalAlign="middle"
                          ml="6px"
                          data-testid="starred-expiry"
                          role="img"
                          aria-label={t('chat.message.disappears', {
                            time: expiryLabel(message.expiresAt),
                          })}
                        >
                          <Timer size={13} aria-hidden="true" />
                        </Box>
                      )}
                    </Text>
                    <IconButton
                      aria-label={t('apps.starred.unstar')}
                      icon={
                        <Star
                          size={18}
                          fill="var(--chakra-colors-saffron)"
                          color="var(--chakra-colors-saffron-text)"
                        />
                      }
                      size="sm"
                      variant="ghost"
                      colorScheme="gray"
                      onClick={() => void handleUnstar(starred)}
                    />
                  </HStack>
                  <Box
                    as="button"
                    type="button"
                    w="full"
                    textAlign="left"
                    onClick={() => openConversation(starred)}
                    aria-label={t('apps.starred.openIn', {
                      chat: chatName(starred),
                    })}
                  >
                    <HStack
                      spacing="10px"
                      align={
                        isPhoto || message.type === 'voice'
                          ? 'center'
                          : 'flex-start'
                      }
                    >
                      <Avatar
                        name={sender}
                        src={message.sender?.avatarUrl ?? undefined}
                        ownerId={message.sender?.id}
                        familyId={currentFamilyId}
                        size="sm"
                      />
                      {isPhoto && <StarredPhoto message={message} />}
                      {message.type === 'voice' ? (
                        <HStack
                          flex="1"
                          spacing="10px"
                          h="48px"
                          pl="4px"
                          pr="12px"
                          borderRadius="full"
                          bg="surface.subtle"
                        >
                          <Box
                            w="40px"
                            h="40px"
                            borderRadius="full"
                            bg="accent.solid"
                            color="white"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                          >
                            <Play
                              size={16}
                              fill="currentColor"
                              aria-hidden="true"
                            />
                          </Box>
                          <Box
                            flex="1"
                            h="4px"
                            borderRadius="2px"
                            bg="gray.200"
                            aria-hidden="true"
                          />
                          <Text fontSize="13px" color="muted">
                            {formatDuration(meta.duration)}
                          </Text>
                        </HStack>
                      ) : (
                        <VStack align="stretch" spacing="2px" minW={0}>
                          <Text fontWeight={700} fontSize="15px">
                            {sender}
                          </Text>
                          <Text
                            fontSize={isPhoto ? '15px' : '16px'}
                            lineHeight="23px"
                            color={isPhoto ? 'ink.body' : undefined}
                            noOfLines={4}
                          >
                            {isPhoto
                              ? t('apps.starred.photo')
                              : messagePreview(starred, t)}
                          </Text>
                          {isUnbound(starred.message) && <UnboundMark />}
                        </VStack>
                      )}
                    </HStack>
                  </Box>
                </Box>
              );
            })}
            {hasMore && (
              <Button
                variant="outline"
                colorScheme="gray"
                borderColor="line"
                onClick={() => void loadMore()}
                isLoading={loadingMore}
              >
                {t('apps.starred.showMore')}
              </Button>
            )}
          </VStack>
        )}
      </Box>
    </Flex>
  );
}
